/**
 * PURPOSE: Borrador local de una serie/final de Atletismo y su `RoundSave` PARCIAL.
 * RESPONSIBILITIES: Convertir un `Round` del servidor en un borrador editable, comparar el borrador
 *   con la serie de partida y construir el cuerpo de `PUT .../rounds/:roundId` con SOLO lo que cambió
 *   (filas y campos), además de detectar celdas con forma inválida y ubicar el error `INVALID_MARK`
 *   que devuelve el servidor. Puro.
 *
 *   PARCIAL DE VERDAD. El contrato dice que las filas omitidas y los campos omitidos no se tocan. Por
 *   eso una fila solo viaja si cambió, y dentro de ella solo viaja el grupo de campos que cambió:
 *   `mark`+`status` en carreras, `attempts` completos en saltos/lanzamientos, `heights` completas en
 *   salto alto/garrocha, `legs` completas en postas, y `lane`/`order` sueltos.
 *
 *   NADA DE CÁLCULO. El borrador no ordena ni puntúa: `placeInRound`, `best`, `qualified`,
 *   `advancesToFinal`... se leen de la respuesta del servidor tras guardar.
 * COLLABORATORS: utils/athleticsMarks.js, area/AtletismoResultadosArea/RoundEditor.jsx.
 * ROUTE: winscore/src/utils/athleticsRoundDraft.js
 */
import {
  DEFAULT_ATTEMPTS_PER_HEIGHT,
  MANUAL_STATUS_CODES,
  TRACK_STATUS_CODES,
  interpretTrackMarkTyping,
  isValidAttemptResult,
  isValidHeightAttempts,
  isValidTimeMark,
  isValidWind,
  normalizeAttemptResult,
  normalizeTimeMark,
  normalizeWind,
  sanitizeAttemptTyping,
  sanitizeHeightAttempts,
} from "./athleticsMarks.js";

export const TRACK_FORMATS = Object.freeze(["CPT", "MAR", "PST"]);

const asText = (value) => (value === null || value === undefined ? "" : String(value));

const toIntOrNull = (value) => {
  const text = asText(value).trim();
  if (!text) return null;
  const number = Number(text);
  return Number.isInteger(number) ? number : null;
};

const textOrNull = (value) => {
  const text = asText(value).trim();
  return text ? text : null;
};

/** Número de casillas de intento: `config.attempts`, o 3 en sub-pruebas de combinadas y 6 en el resto. */
export const resolveAttemptSlots = (prueba) => {
  const configured = Number(prueba?.config?.attempts);
  if (Number.isInteger(configured) && configured > 0) return configured;
  return prueba?.parentId ? 3 : 6;
};

/**
 * Saltos con UN solo resultado por participante (SHL configurado con 1 intento): la grilla muestra una única celda
 * "Marca" y el servidor toma esa marca como la mejor.
 */
export const isSingleMarkPrueba = (prueba) => prueba?.format === "SHL" && resolveAttemptSlots(prueba) === 1;

/** Intentos por altura (salto alto y garrocha): `config.attemptsPerHeight` (1-9) o 3. */
export const resolveAttemptsPerHeight = (prueba) => {
  const value = Number(prueba?.config?.attemptsPerHeight);
  return Number.isInteger(value) && value >= 1 && value <= 9 ? value : DEFAULT_ATTEMPTS_PER_HEIGHT;
};

/** Relevistas con posta asignada, ordenados por posta. */
export const legsFromParticipant = (participant) =>
  (Array.isArray(participant?.members) ? participant.members : [])
    .filter((member) => member && member.entryId !== null && member.entryId !== undefined)
    .filter((member) => Number.isInteger(Number(member.leg)) && Number(member.leg) >= 1)
    .map((member) => ({ entryId: Number(member.entryId), leg: Number(member.leg) }))
    .sort((a, b) => a.leg - b.leg);

/** Marca editable de una fila de carrera: solo si el servidor tiene una marca real (`markValue`). */
const initialTrackMark = (entry) =>
  entry?.markValue !== null && entry?.markValue !== undefined && entry?.mark ? String(entry.mark) : "";

/**
 * Estado editable de la fila. En SHL/SVA solo los manuales (DNS/DNF/DQ) son del operador; `OK`,
 * `NM` y `NH` los calcula el servidor con los intentos y en el borrador valen "automático" (null).
 */
const initialRowStatus = (entry, format) => {
  const status = entry?.status ?? null;
  if (format === "SHL" || format === "SVA") return MANUAL_STATUS_CODES.includes(status) ? status : null;
  return status;
};

export const buildRowDraft = (entry, { attemptSlots = 6, format } = {}) => {
  const attempts = Array.isArray(entry?.attempts) ? entry.attempts : [];
  const maxNo = attempts.reduce((max, attempt) => Math.max(max, Number(attempt?.no) || 0), 0);
  // Con un solo resultado se edita solo esa celda; al guardarla, el servidor reemplaza cualquier intento anterior.
  const slots = format === "SHL" && attemptSlots === 1 ? 1 : Math.max(attemptSlots, maxNo);
  const attemptDraft = Array.from({ length: slots }, (_, index) => {
    const found = attempts.find((attempt) => Number(attempt?.no) === index + 1);
    return {
      no: index + 1,
      result: asText(found?.result),
      wind: asText(found?.wind),
    };
  });

  const heights = {};
  (Array.isArray(entry?.heights) ? entry.heights : []).forEach((item) => {
    const cm = Number(item?.heightCm);
    if (Number.isFinite(cm)) heights[cm] = asText(item?.attempts);
  });

  return {
    id: entry?.id,
    lane: asText(entry?.lane),
    order: asText(entry?.order),
    mark: initialTrackMark(entry),
    status: initialRowStatus(entry, format),
    attempts: attemptDraft,
    heights,
    legs: legsFromParticipant(entry?.participant),
  };
};

export const buildRoundDraft = (round, options = {}) => {
  const rows = {};
  (Array.isArray(round?.entries) ? round.entries : []).forEach((entry) => {
    rows[entry.id] = buildRowDraft(entry, options);
  });
  return {
    wind: asText(round?.wind),
    status: round?.status ?? "pending",
    heights: (Array.isArray(round?.heights) ? round.heights : [])
      .map(Number)
      .filter(Number.isFinite),
    attemptsPerHeight: options.attemptsPerHeight ?? DEFAULT_ATTEMPTS_PER_HEIGHT,
    rows,
  };
};

// ------------------------------------------ cambios de una fila ------------------------------------------

/**
 * Celda de marca de una carrera. Escribir un tiempo deja la fila en OK; borrarlo con estado OK la
 * deja sin resultado; escribir DNS/DNF/DQ cambia el estado y vacía la marca.
 */
export const trackMarkPatch = (row, raw, statusCodes = TRACK_STATUS_CODES) => {
  const result = interpretTrackMarkTyping(raw, statusCodes);
  if (result.status) return { mark: "", status: result.status };
  const patch = { mark: result.mark };
  if (!result.pending) {
    if (result.mark.trim() && row?.status !== "OK") patch.status = "OK";
    if (!result.mark.trim() && row?.status === "OK") patch.status = null;
  }
  return patch;
};

/** Selector de estado de una carrera: DNS/DNF/DQ vacían la marca; OK y "—" la conservan. */
export const trackStatusPatch = (status) => {
  const value = status || null;
  if (value && value !== "OK") return { status: value, mark: "" };
  return { status: value };
};

const padAttempts = (attempts, index) => {
  const list = Array.isArray(attempts) ? [...attempts] : [];
  while (list.length <= index) list.push({ no: list.length + 1, result: "", wind: "" });
  return list;
};

export const attemptFieldPatch = (row, index, field, value) => ({
  attempts: padAttempts(row?.attempts, index).map((attempt, position) =>
    position === index ? { ...attempt, [field]: value } : attempt
  ),
});

/** Intento de salto/lanzamiento. Escribir una marca en una fila DNS le quita el DNS. */
export const attemptResultPatch = (row, index, raw) => {
  const result = sanitizeAttemptTyping(raw);
  const patch = attemptFieldPatch(row, index, "result", result);
  if (row?.status === "DNS" && result !== "") patch.status = null;
  return patch;
};

/** Celda de altura (O/X/-, hasta `maxAttempts`). Escribir intentos en una fila DNS le quita el DNS. */
export const heightAttemptsPatch = (row, heightCm, raw, maxAttempts = DEFAULT_ATTEMPTS_PER_HEIGHT) => {
  const value = sanitizeHeightAttempts(raw, maxAttempts);
  const patch = { heights: { ...(row?.heights || {}), [heightCm]: value } };
  if (row?.status === "DNS" && value !== "") patch.status = null;
  return patch;
};

/**
 * ¿Se puede escribir el intento `index` (0-based)? Los intentos 4-6 solo para quien el servidor
 * marcó con `advancesToFinal` (8 mejores tras 3 intentos) o para quien ya tiene intentos >3. Con
 * `allAttempts` (`config.allAttempts` de la prueba, p. ej. martillo) todos están siempre habilitados.
 */
export const isAttemptEnabled = (entry, row, index, { allAttempts = false } = {}) => {
  if (index < 3 || allAttempts) return true;
  if (entry?.advancesToFinal === true) return true;
  const serverHasLater = (entry?.attempts || []).some((attempt) => Number(attempt?.no) > 3);
  const draftHasLater = (row?.attempts || []).some(
    (attempt, position) => position >= 3 && asText(attempt?.result).trim() !== ""
  );
  return serverHasLater || draftHasLater;
};

// ------------------------------------------ tablero de carriles ------------------------------------------

const laneOf = (row) => {
  const lane = toIntOrNull(row?.lane);
  return lane && lane > 0 ? lane : null;
};

/** Carriles del tablero: al menos 8 (o `config.lanes`), más si alguna fila ya usa uno mayor. */
export const resolveLaneCount = (prueba, draft) =>
  Math.max(
    Number(prueba?.config?.lanes) || 8,
    ...Object.values(draft?.rows || {}).map((row) => laneOf(row) || 0)
  );

/**
 * Tablero de carriles a partir del borrador: un puesto por carril (`entry` o null si está libre) y las filas de la
 * serie sin carril (o con un carril repetido: la primera lo ocupa, las demás quedan aquí para reubicarlas).
 */
export const buildLaneBoard = (entries, draft, laneCount) => {
  const byLane = new Map();
  const unplaced = [];
  (Array.isArray(entries) ? entries : []).forEach((entry) => {
    const row = draft?.rows?.[entry.id];
    if (!row) return;
    const lane = laneOf(row);
    if (lane && lane <= laneCount && !byLane.has(lane)) byLane.set(lane, entry);
    else unplaced.push(entry);
  });
  const lanes = Array.from({ length: laneCount }, (_, index) => ({ lane: index + 1, entry: byLane.get(index + 1) || null }));
  return { lanes, unplaced };
};

/**
 * Mueve una fila del borrador a un carril. Si el carril está ocupado, el ocupante pasa al carril de origen (intercambio;
 * queda sin carril si el que llega no tenía). `lane` vacío = sin carril (sigue en la serie).
 */
export const assignLaneInDraft = (draft, rowId, lane) => {
  const rows = draft?.rows || {};
  const row = rows[rowId];
  if (!row) return draft;
  const target = toIntOrNull(lane);
  const from = laneOf(row);
  if ((target || null) === from) return draft;
  const next = { ...rows, [rowId]: { ...row, lane: target ? String(target) : "" } };
  if (target) {
    Object.keys(rows).forEach((key) => {
      if (String(key) !== String(rowId) && laneOf(rows[key]) === target) {
        next[key] = { ...rows[key], lane: from ? String(from) : "" };
      }
    });
  }
  return { ...draft, rows: next };
};

/** Claves de celdas con forma inválida para consulta rápida desde la grilla. */
export const invalidCellKeys = (cells) =>
  new Set(
    (cells || []).map((cell) => {
      if (cell.field === "wind") return "round|wind";
      if (cell.field === "height") return `${cell.rowId}|height|${cell.heightCm}`;
      if (cell.field === "attempt" || cell.field === "attemptWind") {
        return `${cell.rowId}|${cell.field}|${cell.index}`;
      }
      return `${cell.rowId}|${cell.field}`;
    })
  );

// --------------------------------------------- comparación ---------------------------------------------

const normalizedTrackMark = (value) => textOrNull(normalizeTimeMark(value));

const normalizeAttemptsForSave = (attempts, windMode) =>
  (Array.isArray(attempts) ? attempts : [])
    .map((attempt) => ({ ...attempt, result: normalizeAttemptResult(attempt?.result) }))
    .filter((attempt) => attempt.result !== "")
    .map((attempt) => {
      const item = { no: Number(attempt.no), result: attempt.result };
      if (windMode === "attempt") {
        const wind = normalizeWind(attempt.wind);
        if (wind !== null) item.wind = wind;
      }
      return item;
    });

const normalizeHeightsForSave = (heights) =>
  Object.keys(heights || {})
    .map((key) => ({ heightCm: Number(key), attempts: asText(heights[key]).trim().toUpperCase() }))
    .filter((item) => Number.isFinite(item.heightCm) && item.attempts !== "")
    .sort((a, b) => a.heightCm - b.heightCm);

const normalizeLegs = (legs) =>
  (Array.isArray(legs) ? legs : [])
    .filter((leg) => leg && leg.entryId !== null && leg.entryId !== undefined && leg.leg)
    .map((leg) => ({ entryId: Number(leg.entryId), leg: Number(leg.leg) }))
    .sort((a, b) => a.leg - b.leg);

const sameJson = (left, right) => JSON.stringify(left) === JSON.stringify(right);

/**
 * Cambios de UNA fila. `null` si no cambió nada.
 * - Carreras: si cambia la marca o el estado viajan los dos. Una marca sin estado se omite el
 *   `status` (el servidor la deja en `OK`); sin marca y sin estado se manda `null`/`null` (limpia).
 */
export const diffRowDraft = (base, draft, { format, windMode } = {}) => {
  if (!base || !draft) return null;
  const out = { id: draft.id ?? base.id };

  const lane = toIntOrNull(draft.lane);
  if (lane !== toIntOrNull(base.lane)) out.lane = lane;
  const order = toIntOrNull(draft.order);
  if (order !== toIntOrNull(base.order)) out.order = order;

  if (TRACK_FORMATS.includes(format)) {
    const mark = normalizedTrackMark(draft.mark);
    const status = draft.status ?? null;
    if (mark !== normalizedTrackMark(base.mark) || status !== (base.status ?? null)) {
      out.mark = mark;
      if (status !== null || mark === null) out.status = status;
    }
    if (format === "PST") {
      const legs = normalizeLegs(draft.legs);
      if (!sameJson(legs, normalizeLegs(base.legs))) out.legs = legs;
    }
  } else if (format === "SHL") {
    const attempts = normalizeAttemptsForSave(draft.attempts, windMode);
    if (!sameJson(attempts, normalizeAttemptsForSave(base.attempts, windMode))) out.attempts = attempts;
    if ((draft.status ?? null) !== (base.status ?? null)) out.status = draft.status ?? null;
  } else if (format === "SVA") {
    const heights = normalizeHeightsForSave(draft.heights);
    if (!sameJson(heights, normalizeHeightsForSave(base.heights))) out.heights = heights;
    if ((draft.status ?? null) !== (base.status ?? null)) out.status = draft.status ?? null;
  }

  return Object.keys(out).length > 1 ? out : null;
};

/**
 * `RoundSave` parcial a partir de la serie de partida (`baselineRound`, la que llegó del servidor)
 * y el borrador. Siempre lleva `expectedRevision`; el resto solo si cambió.
 */
export const buildRoundSave = ({
  baselineRound,
  draft,
  format,
  windMode,
  expectedRevision,
  attemptSlots = 6,
  attemptsPerHeight = DEFAULT_ATTEMPTS_PER_HEIGHT,
}) => {
  const base = buildRoundDraft(baselineRound, { attemptSlots, format, attemptsPerHeight });
  const payload = { expectedRevision };

  if (windMode === "round") {
    const wind = normalizeWind(draft?.wind);
    if (wind !== normalizeWind(base.wind)) payload.wind = wind;
  }
  if ((draft?.status ?? null) !== (base.status ?? null)) payload.status = draft.status;
  if (format === "SVA") {
    const heights = (draft?.heights || []).map(Number);
    if (!sameJson(heights, base.heights)) payload.heights = heights;
    const perHeight = Number(draft?.attemptsPerHeight) || base.attemptsPerHeight;
    if (perHeight !== base.attemptsPerHeight) payload.attemptsPerHeight = perHeight;
  }

  const entries = [];
  (Array.isArray(baselineRound?.entries) ? baselineRound.entries : []).forEach((entry) => {
    const change = diffRowDraft(base.rows[entry.id], draft?.rows?.[entry.id], { format, windMode });
    if (change) entries.push(change);
  });
  if (entries.length) payload.entries = entries;
  return payload;
};

export const hasRoundSaveChanges = (payload) =>
  Object.keys(payload || {}).some((key) => key !== "expectedRevision");

/** Filas con cambios, para marcarlas en la grilla. */
export const changedRowIds = (payload) =>
  new Set((payload?.entries || []).map((entry) => entry.id));

// ------------------------------------------- validación local -------------------------------------------

/**
 * Celdas cuya FORMA no es válida (no reglas de negocio). Se resaltan y bloquean el guardado para no
 * mandar al servidor algo que seguro rechazará con `INVALID_MARK`.
 * @returns {Array<{ rowId, field, index? }>}
 */
export const collectInvalidCells = ({ draft, format, windMode }) => {
  const invalid = [];
  if (windMode === "round" && !isValidWind(normalizeWind(draft?.wind) ?? "")) {
    invalid.push({ rowId: null, field: "wind" });
  }
  Object.values(draft?.rows || {}).forEach((row) => {
    if (TRACK_FORMATS.includes(format)) {
      const mark = normalizeTimeMark(row.mark);
      // "OK" sin marca también es inválido: el servidor lo rechaza con INVALID_MARK.
      if (!isValidTimeMark(mark) || (row.status === "OK" && !mark)) {
        invalid.push({ rowId: row.id, field: "mark" });
      }
    } else if (format === "SHL") {
      (row.attempts || []).forEach((attempt, index) => {
        if (!isValidAttemptResult(normalizeAttemptResult(attempt.result))) {
          invalid.push({ rowId: row.id, field: "attempt", index });
        }
        if (windMode === "attempt" && !isValidWind(normalizeWind(attempt.wind) ?? "")) {
          invalid.push({ rowId: row.id, field: "attemptWind", index });
        }
      });
    } else if (format === "SVA") {
      Object.keys(row.heights || {}).forEach((heightCm) => {
        if (!isValidHeightAttempts(row.heights[heightCm], draft?.attemptsPerHeight)) {
          invalid.push({ rowId: row.id, field: "height", heightCm: Number(heightCm) });
        }
      });
    }
  });
  return invalid;
};

// --------------------------------------- error del servidor ---------------------------------------

/**
 * Ubica el `INVALID_MARK` del servidor (`details: { entryId, field, value }`). `entryId` se busca
 * primero como id de la fila de la serie (el `id` que viaja en `RoundSave.entries`) y, si no
 * aparece, como `participant.entryId` de la inscripción: el contrato no fija cuál de los dos es.
 * @returns {{ rowId, field, value } | null}
 */
export const resolveInvalidMarkTarget = (details, entries) => {
  if (!details || typeof details !== "object") return null;
  const list = Array.isArray(entries) ? entries : [];
  const wanted = Number(details.entryId);
  let row = null;
  if (Number.isFinite(wanted)) {
    row =
      list.find((entry) => Number(entry?.id) === wanted) ||
      list.find((entry) => Number(entry?.participant?.entryId) === wanted) ||
      null;
  }
  return {
    rowId: row ? row.id : null,
    field: typeof details.field === "string" ? details.field : null,
    value: details.value === undefined ? null : details.value,
  };
};

/**
 * ¿La celda (`kind`: mark | attempt | attemptWind | height | wind) es la que el servidor marcó?
 * El servidor nombra el campo `mark`, `attempt<no>`, `wind<no>` (viento de un intento), `h<cm>`
 * (altura) o `wind` (viento de la serie). Con número se compara exacto (`attemptNo`/`heightCm` de la
 * celda); sin número, por familia y por el texto de la celda cuando viene `value`.
 */
export const isServerInvalidCell = (target, { rowId, kind, cellValue, attemptNo, heightCm }) => {
  if (!target) return false;
  const field = asText(target.field).toLowerCase();
  if (kind === "wind" && (rowId === null || rowId === undefined)) {
    return (target.rowId === null || target.rowId === undefined) && /wind/.test(field);
  }
  if (target.rowId === null || target.rowId === undefined || target.rowId !== rowId) return false;

  const digits = /(\d+)/.exec(field);
  const fieldNumber = digits ? Number(digits[1]) : null;
  const valueMatches =
    target.value === null ||
    target.value === undefined ||
    asText(cellValue).trim().toUpperCase() === asText(target.value).trim().toUpperCase();

  if (kind === "attemptWind" || kind === "attempt") {
    const family = kind === "attemptWind" ? /wind/.test(field) : /attempt/.test(field) && !/wind/.test(field);
    if (!family) return false;
    if (fieldNumber !== null && attemptNo !== undefined && attemptNo !== null) return fieldNumber === Number(attemptNo);
    return valueMatches;
  }
  if (kind === "height") {
    if (!/^h\d+$/.test(field) && !/height/.test(field)) return false;
    if (fieldNumber !== null && heightCm !== undefined && heightCm !== null) return fieldNumber === Number(heightCm);
    return valueMatches;
  }
  return !field || /mark/.test(field);
};
